import type { IEditableDataSource } from "@/data/dao/service/BaseServiceDao";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import type { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { UiNotification } from "@/portal/ui/Notification";

export abstract class BaseTabbedController<M> {
    protected $element: JQuery;
    protected $head: JQuery;
    protected $tabs: JQuery;
    protected $tabContent: JQuery;
    protected entityId?: string;
    protected copyEntityId?: string;

    protected dataSource?: IEditableDataSource<M>;
    protected entityForm?: BaseEntityForm<M, any>;

    private modelLoaded = false;
    protected model?: M;

    public onActivateTab?: { (id: string) };

    constructor(element: JQuery, dataSource?: IEditableDataSource<M>) {
        this.$element = element;

        this.dataSource = dataSource;

        this.$head = this.$element.find("> .kt-portlet__head");
        this.$tabs = this.$head.find("> .kt-portlet__head-toolbar > .nav-tabs");
        this.$tabContent = this.$element.find("> .tab-content");

        this.$tabs.on("show.bs.tab", (b) => {
            if (this.onActivateTab !== undefined) this.onActivateTab($(b.target).attr("href").substr(1));
        });

        if (this.dataSource !== undefined) {
            const id = this.$element.attr("data-id");
            const copyId = this.$element.attr("data-copy-id");

            if (id !== undefined && id != null && id.length > 0) this.entityId = id;

            this.copyEntityId = copyId === "" ? undefined : copyId;

            if (this.entityId !== undefined) {
                window.setTimeout(() => {
                    this.loadModel();
                }, 100);
            }

            if (this.copyEntityId !== undefined) {
                window.setTimeout(() => {
                    this.loadCopyModel();
                }, 100);
            }
        }
    }

    public disableTabs(names: Array<string>) {
        let selector = "";
        for (const i in names) selector += '> li > a[href="#' + names[i] + '"], ';

        if (selector.length > 0) selector = selector.substr(0, selector.length - 2);

        this.$tabs.find(selector).addClass("disabled").prop("disabled", true);
    }

    public disableTab(name: string) {
        this.$tabs
            .find('> li > a[href="#' + name + '"]')
            .addClass("disabled")
            .prop("disabled", true);
    }

    public enableTabs(names: Array<string>) {
        let selector = "";
        for (const i in names) selector += '> li > a[href="#' + names[i] + '"], ';

        if (selector.length > 0) selector = selector.substr(0, selector.length - 2);

        this.$tabs.find(selector).removeClass("disabled").prop("disabled", false);
    }

    public enableTab(name: string) {
        this.$tabs
            .find('> li > a[href="#' + name + '"]')
            .removeClass("disabled")
            .prop("disabled", false);
    }

    public activateTab(name) {
        this.$tabs.find('> li > a[href="#' + name + '"]').trigger("click");
    }

    public setHeadLabel(lbl: string) {
        this.$head.find("> .kt-portlet__head-label > .kt-portlet__head-title > small").html(lbl);
    }

    public formContentByTabName(tabName: string): JQuery {
        return this.$tabContent.find("> #" + tabName);
    }

    public registerModelForm<D>(form: BaseEntityForm<M, D>) {
        form.onNameChanged = (newName) => this.setHeadLabel(newName);
        this.entityForm = form;

        if (this.copyEntityId != null) {
            this.loadCopyModel();
        } else {
            this.loadModel();
        }
    }

    private loadModel() {
        if (this.modelLoaded) return;

        this.modelLoaded = true;
        if (this.entityId === undefined || this.dataSource === undefined) {
            this.onModelLoaded();
            return;
        }

        UiNotification.setLoading(true);
        this.dataSource
            .getSingle(this.entityId)
            .then((m) => {
                UiNotification.setLoading(false);
                this.model = m;
                this.entityForm!.setData(m);
                this.onModelLoaded(m);
                this.checkPreselectedTabActivation();
            })
            .catch((ex) => {
                logError(ex);
                UiNotification.showException(i18n("general.failedLoadingData"));
                UiNotification.setLoading(false);
            });
    }

    private loadCopyModel() {
        if (this.modelLoaded) return;

        this.modelLoaded = true;
        if (this.copyEntityId === undefined || this.dataSource === undefined) {
            this.onModelLoaded();
            return;
        }

        UiNotification.setLoading(true);
        this.dataSource
            .getSingle(this.copyEntityId)
            .then((m) => {
                UiNotification.setLoading(false);
                
                m["id"] = undefined;
                
                this.model = m;
                this.entityForm!.setData(m);
                this.onModelLoaded(m);
                this.checkPreselectedTabActivation();
            })
            .catch((ex) => {
                logError(ex);
                UiNotification.showException(i18n("general.failedLoadingData"));
                UiNotification.setLoading(false);
            });
    }

    private checkPreselectedTabActivation() {
        //get hash from url
        if (window.location.hash === undefined || window.location.hash === "") return;

        const tab = this.$tabs.find('[href="' + window.location.hash + '"]');
        if (tab.hasClass("disabled")) return;

        tab["tab"]("show");
    }

    protected abstract onModelLoaded(model?: M);
}
